import { useEffect, useMemo, useState } from "react";
import type { components } from "@cntrl/api-client";
import { selectNowCard, venueNowMinutes } from "./dayOfShow.js";
import styles from "./dayOfShow.module.scss";

type ScheduleItemDto = components["schemas"]["ScheduleItemDto"];

/** Re-render cadence of the clock: a PURE LOCAL render tick — never a WS event, never a push.*/
const TICK_MS = 30_000;

/**
 * The Now card — the flagship live hero. Three structurally-different branches computed in VENUE-LOCAL time
 * ({@link selectNowCard}) and re-rendered on a 30s LOCAL tick: PRE-SHOW (counting to the next row), LIVE (the
 * current phase with a native-feeling progress bar + elapsed/remaining), and WRAPPED (after the last row — no
 * glow, no dot). No network: it reads the already-loaded run-of-show rows. When there is no timed row at all
 * it returns null so the caller can render the "activate the run of show" hint instead.
 */
export function RunOfShowClock({
  items,
  timezone,
  dayDelta = 0,
}: {
  items: readonly ScheduleItemDto[];
  timezone: string | null | undefined;
  /** 0 today, negative past — a past show wraps rather than reading today's clock against its set times.
   *  A FUTURE show is not rendered as a clock at all (the caller shows the day-countdown hero). */
  dayDelta?: number;
}) {
  // The tick just forces a recompute of venue-now every 30s; the value itself is unused.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const nowMin = venueNowMinutes(timezone);
  const card = useMemo(() => selectNowCard(items, nowMin, dayDelta), [items, nowMin, dayDelta]);

  if (!card) return null;

  return (
    <div className={styles.now} data-mode={card.mode} data-testid="run-of-show-clock">
      {card.mode !== "wrapped" && <div className={styles.nowGlow} aria-hidden="true" />}
      <div className={styles.nowHead}>
        <span className={styles.nowEyebrow}>
          {card.mode !== "wrapped" && <span className={styles.livedot} data-tone="accent" aria-hidden="true" />}
          {card.eyebrow}
        </span>
        {card.counter && <span className={styles.nowCounter}>{card.counter}</span>}
      </div>
      <div className={styles.nowTitle}>{card.title}</div>
      <div className={styles.nowSub}>{card.sub}</div>
      {card.progress != null && (
        <div className={styles.nowProgress}>
          <div className={styles.nowProgressBar}>
            <div className={styles.nowProgressFill} style={{ width: `${Math.round(card.progress * 100)}%` }} />
          </div>
          <div className={styles.nowProgressRow}>
            <span>{card.elapsed}</span>
            <span>{card.remaining}</span>
          </div>
        </div>
      )}
    </div>
  );
}
